import React from 'react';
import { MetarData } from '../contexts/MetarContext';
import { detectUnitSystem } from '../lib/utils';
import { useLanguage } from '../contexts/LanguageContext';

const getCategoryColor = (category?: string) => {
  switch (category) {
    case 'VFR':
      return 'bg-green-100 text-green-800 border-green-300';
    case 'MVFR':
      return 'bg-blue-100 text-blue-800 border-blue-300';
    case 'IFR':
      return 'bg-red-100 text-red-800 border-red-300';
    case 'LIFR':
      return 'bg-pink-100 text-pink-800 border-pink-300';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-300';
  }
};

interface MetarDetailsProps {
  metarData: MetarData | null;
  loading: boolean;
  error: string | null;
  icao?: string;
}

const MetarDetails: React.FC<MetarDetailsProps> = ({ metarData, loading, error, icao }) => {
  const { t } = useLanguage();
  const unitSystem = detectUnitSystem();

  // Helper for temperature
  const temp = () => {
    if (!metarData) return '--';
    if (unitSystem === 'imperial' && (metarData as any).temperature_f !== undefined) {
      return `${(metarData as any).temperature_f}°F` + (metarData.temperature !== undefined ? ` / ${metarData.temperature}°C` : '');
    }
    if (metarData.temperature !== undefined) return `${metarData.temperature}°C`;
    return '--';
  };
  const dew = () => {
    if (!metarData) return '--';
    if (unitSystem === 'imperial' && (metarData as any).dewpoint_f !== undefined) {
      return `${(metarData as any).dewpoint_f}°F` + (metarData.dewpoint !== undefined ? ` / ${metarData.dewpoint}°C` : '');
    }
    if (metarData.dewpoint !== undefined) return `${metarData.dewpoint}°C`;
    return '--';
  };
  // Helper for visibility
  const vis = () => {
    if (!metarData) return '--';
    if (unitSystem === 'imperial' && (metarData as any).visibility_miles !== undefined) {
      return `${(metarData as any).visibility_miles} mi` + (metarData.visibility !== undefined ? ` / ${(metarData.visibility/1000).toFixed(1)} km` : '');
    }
    if (metarData.visibility !== undefined) return `${(metarData.visibility/1000).toFixed(1)} km`;
    return '--';
  };
  // Helper for pressure
  const pressure = () => {
    if (!metarData) return '--';
    if (unitSystem === 'imperial' && (metarData as any).pressure_inhg !== undefined) {
      return `${(metarData as any).pressure_inhg} inHg` + (metarData.pressure !== undefined ? ` / ${metarData.pressure} hPa` : '');
    }
    if (metarData.pressure !== undefined) return `${metarData.pressure} hPa`;
    return '--';
  };

  return (
    <div className="w-full max-w-2xl">
      {loading ? (
        <div className="space-y-2">
          <div className="flex items-center space-x-2 mb-2">
            <div className="h-6 w-12 bg-gray-200 rounded animate-pulse"></div>
            {icao && <span className="text-sm font-semibold text-gray-700">{icao}</span>}
            <span className="text-xs text-gray-500">{t('common.currentMetar')}</span>
          </div>
          <div className="h-16 bg-gray-200 rounded animate-pulse"></div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </div>
      ) : error ? (
        <div className="text-red-600 text-sm">Error: {error}</div>
      ) : metarData ? (
        <>
          <div className="flex items-center space-x-2 mb-2">
            <span className={`border text-xs font-bold rounded px-2 py-0.5 uppercase ${getCategoryColor(metarData.flight_category)}`}
              title={metarData.flight_category || 'Unknown'}
              style={{ minWidth: 40, textAlign: 'center' }}>
              {metarData.flight_category || '--'}
            </span>
            {icao && <span className="text-sm font-semibold text-gray-700">{icao}</span>}
            <span className="text-xs text-gray-500">{t('common.currentMetar')}</span>
          </div>
          <div className="space-y-2">
            <div className="font-mono text-xs bg-gray-50 rounded p-2 border border-gray-100 mb-2">
              {metarData.raw}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <div><span className="font-semibold">{t('metar.flightCategory')}:</span> {metarData.flight_category || '--'}</div>
              <div><span className="font-semibold">{t('metar.observed')}:</span> {metarData.time ? new Date(metarData.time).toLocaleString() : '--'}</div>
              <div><span className="font-semibold">{t('metar.wind')}:</span> {metarData.wind_direction !== undefined ? `${metarData.wind_direction}°` : '--'} {metarData.wind_speed !== undefined ? `${metarData.wind_speed}kt` : ''} {metarData.wind_gust ? `(G${metarData.wind_gust})` : ''}</div>
              <div><span className="font-semibold">{t('metar.visibility')}:</span> {vis()}</div>
              <div><span className="font-semibold">{t('metar.tempDewpoint')}:</span> {temp()} / {dew()}</div>
              <div><span className="font-semibold">{t('metar.altimeter')}:</span> {pressure()}</div>
              <div className="col-span-2"><span className="font-semibold">{t('metar.weather')}:</span> {metarData.weather && metarData.weather.length > 0 ? metarData.weather.join(', ') : '--'}</div>
              <div className="col-span-2"><span className="font-semibold">{t('metar.clouds')}:</span> {metarData.clouds && metarData.clouds.length > 0 ? metarData.clouds.join(', ') : '--'}</div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-xs text-gray-500">{t('common.noData')}</div>
      )}
    </div>
  );
};

export default MetarDetails; 